// Application state as Preact signals. Data → filters → derived lists; the map and
// the list both read the derived signals, so they can never disagree.
import { signal, computed, batch } from "@preact/signals";
import type { City, EquineData, EventFeed, EventItem, Feature, FixturesData, FoodPoint, LayerId, LayerPoint, Site, SortKey } from "../types";
import { LAYERS, LAYER_ORDER, TYPE_GROUPS } from "../data/layers";
import { FEEDS, FEED_ORDER, equineToEvent, facetKey, fixtureToEvent, inWindow } from "../data/events";
import { scoreFeature, type Match } from "../data/search";
import { haversineKm, inBBox, type BBox } from "../utils/geo";
import { tokenize } from "../utils/format";

// ---- Data ----
export const cities = signal<City[]>([]);
export const featuresByLayer = signal<Record<LayerId, Feature[]>>({ history: [], nature: [], gaa: [], equine: [], food: [], sessions: [], trains: [] });
export const layerLoaded = signal<Record<LayerId, boolean>>({ history: false, nature: false, gaa: false, equine: false, food: false, sessions: false, trains: false });
export const dataError = signal<string | null>(null);

export const featuresByKey = computed(() => {
  const m = new Map<string, Feature>();
  for (const id of LAYER_ORDER) for (const f of featuresByLayer.value[id]) m.set(f.key, f);
  return m;
});

// ---- Layers ----
const defaultLayers = new Set<LayerId>(LAYER_ORDER.filter((id) => LAYERS[id].defaultOn));
export const enabledLayers = signal<Set<LayerId>>(new Set(defaultLayers));
export function isLayerOn(id: LayerId): boolean { return enabledLayers.value.has(id); }
export function toggleLayer(id: LayerId, on?: boolean): void {
  const next = new Set(enabledLayers.value);
  const want = on ?? !next.has(id);
  if (want) next.add(id); else next.delete(id);
  enabledLayers.value = next;
}

// ---- Filters ----
export const q = signal("");
export const searchTokens = computed(() => tokenize(q.value));
export const dateFrom = signal<number | null>(null);
export const dateTo = signal<number | null>(null);
export const periods = signal<Set<string>>(new Set());
export const access = signal<Set<string>>(new Set());
export const facilities = signal<Set<string>>(new Set());
export const typeGroups = signal<Set<string>>(new Set());
export const noLivestock = signal(false);
export const freeOnly = signal(false);
export const wheelchair = signal(false);
/** Per-layer "kind" facets for nature / gaa / food. */
export const kinds = signal<Record<string, Set<string>>>({ nature: new Set(), gaa: new Set(), equine: new Set(), food: new Set(), trains: new Set() });
export const dishes = signal<Set<string>>(new Set());
/** Food: meals served (breakfast / lunch / dinner / snack / late). */
export const meals = signal<Set<string>>(new Set());
/** County facet — applies to every layer (Feature.county). */
export const counties = signal<Set<string>>(new Set());
export const radiusKm = signal<number | null>(null);
export type CenterMode = { kind: "city"; idx: number } | { kind: "me"; lat: number; lon: number } | null;
export const centerMode = signal<CenterMode>(null);
export const savedOnly = signal(false);
/** When true the list shows only what's inside the current map view. */
export const followMap = signal(true);
export const sort = signal<SortKey>("name");

export function toggleInSet(sig: { value: Set<string> }, v: string, on?: boolean): void {
  const next = new Set(sig.value);
  const want = on ?? !next.has(v);
  if (want) next.add(v); else next.delete(v);
  sig.value = next;
}
export function toggleKind(layer: string, v: string): void {
  const cur = kinds.value[layer] ?? new Set<string>();
  const next = new Set(cur);
  if (next.has(v)) next.delete(v); else next.add(v);
  kinds.value = { ...kinds.value, [layer]: next };
}

export function clearFilters(): void {
  batch(() => {
    q.value = "";
    dateFrom.value = null;
    dateTo.value = null;
    periods.value = new Set();
    access.value = new Set();
    facilities.value = new Set();
    typeGroups.value = new Set();
    noLivestock.value = false;
    freeOnly.value = false;
    wheelchair.value = false;
    kinds.value = { nature: new Set(), gaa: new Set(), equine: new Set(), food: new Set(), trains: new Set() };
    dishes.value = new Set();
    meals.value = new Set();
    counties.value = new Set();
    radiusKm.value = null;
    centerMode.value = null;
    savedOnly.value = false;
    eventFrom.value = null;
    eventTo.value = null;
    eventFeeds.value = new Set();
    eventCats.value = new Set();
    eventBands.value = new Set();
    eventVenueName.value = null;
  });
}

export const radiusCenter = computed<{ lat: number; lon: number } | null>(() => {
  const c = centerMode.value;
  if (!c) return null;
  if (c.kind === "me") return { lat: c.lat, lon: c.lon };
  const city = cities.value[c.idx];
  return city ? { lat: city.lat, lon: city.lon } : null;
});

export const activeFilterCount = computed(() => {
  let n = 0;
  if (q.value) n++;
  if (dateFrom.value != null || dateTo.value != null) n++;
  if (radiusKm.value && radiusCenter.value) n++;
  if (savedOnly.value) n++;
  if (eventFrom.value != null || eventTo.value != null) n++;
  if (noLivestock.value) n++;
  if (freeOnly.value) n++;
  if (wheelchair.value) n++;
  n += periods.value.size + access.value.size + facilities.value.size + typeGroups.value.size + dishes.value.size + meals.value.size + counties.value.size;
  n += eventFeeds.value.size + eventCats.value.size + eventBands.value.size;
  for (const s of Object.values(kinds.value)) n += s.size;
  return n;
});

// ---- Map ----
export const mapBounds = signal<BBox | null>(null);
export interface FocusRequest { seq: number; lat: number; lon: number; zoom?: number; key?: string; bbox?: BBox }
export const mapFocus = signal<FocusRequest | null>(null);
let focusSeq = 0;
export function focusOn(f: Feature, zoom?: number): void {
  mapFocus.value = { seq: ++focusSeq, lat: f.lat, lon: f.lon, zoom, key: f.key };
}
export function focusBBox(bbox: BBox): void {
  mapFocus.value = { seq: ++focusSeq, lat: (bbox[1] + bbox[3]) / 2, lon: (bbox[0] + bbox[2]) / 2, bbox };
}
export const myLocation = signal<{ lat: number; lon: number } | null>(null);

export type Theme = "light" | "dark";
function initialTheme(): Theme {
  try {
    const t = localStorage.getItem("ihm:theme");
    if (t === "light" || t === "dark") return t;
  } catch { /* ignore */ }
  return matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
export const theme = signal<Theme>(initialTheme());
export function toggleTheme(): void {
  theme.value = theme.value === "dark" ? "light" : "dark";
  try { localStorage.setItem("ihm:theme", theme.value); } catch { /* ignore */ }
}

// ---- Track overlay (data/rail.geojson; the basemap only has rail from z11 up) ----
export const railLinesOn = signal(true);
/** Track is drawn only while the Trains layer itself is on — it is that layer's context. */
export const showRailLines = computed(() => railLinesOn.value && enabledLayers.value.has("trains"));
/** The lines Ireland used to have — disused, abandoned and razed alignments. Off by default. */
export const closedLinesOn = signal(false);
export const showClosedLines = computed(() => closedLinesOn.value && enabledLayers.value.has("trains"));

// ---- Live trains overlay (Irish Rail realtime positions, proxied via /api/trains/live) ----
export const liveTrainsOn = signal(false);
export const liveTrainsCount = signal(0);
export const liveTrainsError = signal<string | null>(null);

// ---- Selection / UI ----
export const selectedKey = signal<string | null>(null);
export const selected = computed<Feature | null>(() => (selectedKey.value ? featuresByKey.value.get(selectedKey.value) ?? null : null));
export function select(f: Feature | null, opts: { focus?: boolean } = {}): void {
  batch(() => {
    selectedKey.value = f ? f.key : null;
    if (f) selectedVenue.value = null;
    if (f && opts.focus) focusOn(f);
  });
}
/** Mobile: whether the filter drawer is open. */
export const filtersOpen = signal(false);
/** Which side-panel section is expanded (desktop accordion state). */
export const filtersCollapsed = signal(false);
export const listScrollRequest = signal(0);

// ---- Saved (starred) ----
const SAVED_KEY = "ihm:saved:v2";
const LEGACY_SAVED_KEY = "ihm:saved";
function loadSaved(): Set<string> {
  const out = new Set<string>();
  try {
    const raw = localStorage.getItem(SAVED_KEY);
    if (raw) for (const k of JSON.parse(raw)) if (typeof k === "string") out.add(k);
    else {
      const legacy = localStorage.getItem(LEGACY_SAVED_KEY);
      if (legacy) for (const id of JSON.parse(legacy)) if (typeof id === "string") out.add(`history:${id}`);
    }
  } catch { /* ignore */ }
  return out;
}
export const saved = signal<Set<string>>(loadSaved());
export function toggleSaved(key: string): void {
  const next = new Set(saved.value);
  if (next.has(key)) next.delete(key); else next.add(key);
  saved.value = next;
  try { localStorage.setItem(SAVED_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
}

// ---- Derived: filtering ----
export interface Scored {
  f: Feature;
  score: number;
  primary: Match["primary"];
  /** km from the radius centre (or from "me" when sorting by distance). */
  dist: number | null;
}

function typeGroupMatch(site: Site, groups: Set<string>): boolean {
  for (const g of groups) {
    const def = TYPE_GROUPS.find((x) => x.id === g);
    if (def && site.types.some((t) => def.types.includes(t))) return true;
  }
  return false;
}

function passesHistory(s: Site): boolean {
  const lo = dateFrom.value, hi = dateTo.value;
  if (lo != null || hi != null) {
    const ds = s.date_start ?? -100000, de = s.date_end ?? 100000;
    if (de < (lo ?? -100000) || ds > (hi ?? 100000)) return false;
  }
  if (periods.value.size && !s.periods.some((p) => periods.value.has(p))) return false;
  if (access.value.size && !access.value.has(s.accessibility ?? "")) return false;
  if (facilities.value.size && !facilities.value.has(s.facilities_level ?? "")) return false;
  if (typeGroups.value.size && !typeGroupMatch(s, typeGroups.value)) return false;
  if (noLivestock.value && (s.walking?.livestock_present || s.walking?.cattle_present)) return false;
  if (freeOnly.value && s.facilities?.admission_fee) return false;
  if (wheelchair.value && !s.facilities?.wheelchair_accessible) return false;
  return true;
}

function passesLayer(f: Feature): boolean {
  if (f.layer === "history") return passesHistory(f.raw as Site);
  if (f.layer === "sessions") return true;
  const p = f.raw as LayerPoint & FoodPoint;
  const ks = kinds.value[f.layer];
  if (ks && ks.size && !(p.kinds || []).some((k) => ks.has(k))) return false;
  if (f.layer === "food" && dishes.value.size && !(p.dishes || []).some((d) => dishes.value.has(d))) return false;
  if (f.layer === "food" && meals.value.size && !(p.meal || []).some((m) => meals.value.has(m))) return false;
  return true;
}

/** Every enabled layer, filtered by the global + layer facets and scored by the query. */
export const filteredByLayer = computed<Record<LayerId, Scored[]>>(() => {
  const tokens = searchTokens.value;
  const centre = radiusKm.value ? radiusCenter.value : null;
  const r = radiusKm.value ?? 0;
  const me = myLocation.value;
  const out = { history: [], nature: [], gaa: [], equine: [], food: [], sessions: [], trains: [] } as Record<LayerId, Scored[]>;
  for (const id of LAYER_ORDER) {
    if (!enabledLayers.value.has(id)) continue;
    for (const f of featuresByLayer.value[id]) {
      if (savedOnly.value && !saved.value.has(f.key)) continue;
      if (counties.value.size && !(f.county && counties.value.has(f.county))) continue;
      if (!passesLayer(f)) continue;
      let dist: number | null = null;
      if (centre) {
        dist = haversineKm(f.lat, f.lon, centre.lat, centre.lon);
        if (dist > r) continue;
      } else if (me) {
        dist = haversineKm(f.lat, f.lon, me.lat, me.lon);
      }
      let score = 0, primary: Match["primary"] = null;
      if (tokens.length) {
        const m = scoreFeature(f, tokens);
        if (!m.score) continue;
        score = m.score; primary = m.primary;
      }
      out[id].push({ f, score, primary, dist });
    }
  }
  return out;
});

export const filteredAll = computed<Scored[]>(() => {
  const out: Scored[] = [];
  for (const id of LAYER_ORDER) out.push(...filteredByLayer.value[id]);
  return out;
});

/** What the list shows: the filtered set, further clipped to the map view unless searching. */
export const visibleAll = computed<Scored[]>(() => {
  const b = mapBounds.value;
  if (!followMap.value || !b || searchTokens.value.length) return filteredAll.value;
  return filteredAll.value.filter((x) => inBBox(x.f.lat, x.f.lon, b));
});

export const listItems = computed<Scored[]>(() => {
  const arr = visibleAll.value.slice();
  let s = sort.value;
  if (s === "relevance" && !searchTokens.value.length) s = "name";
  if (s === "distance" && !arr.some((x) => x.dist != null)) s = "name";
  const byName = (a: Scored, b: Scored) => a.f.name.localeCompare(b.f.name, "en");
  switch (s) {
    case "date_asc": arr.sort((a, b) => (a.f.dateStart ?? 99999) - (b.f.dateStart ?? 99999) || byName(a, b)); break;
    case "date_desc": arr.sort((a, b) => (b.f.dateEnd ?? -99999) - (a.f.dateEnd ?? -99999) || byName(a, b)); break;
    case "distance": arr.sort((a, b) => (a.dist ?? Infinity) - (b.dist ?? Infinity) || byName(a, b)); break;
    case "relevance": arr.sort((a, b) => b.score - a.score || byName(a, b)); break;
    default: arr.sort(byName);
  }
  return arr;
});

export const layerCounts = computed<Record<LayerId, { total: number; filtered: number }>>(() => {
  const out = {} as Record<LayerId, { total: number; filtered: number }>;
  for (const id of LAYER_ORDER) out[id] = { total: featuresByLayer.value[id].length, filtered: filteredByLayer.value[id].length };
  return out;
});

/** Counties present across loaded layers, with counts, for the facet chips. */
export const countyCounts = computed<[string, number][]>(() => {
  const m = new Map<string, number>();
  for (const id of LAYER_ORDER) for (const f of featuresByLayer.value[id]) if (f.county) m.set(f.county, (m.get(f.county) ?? 0) + 1);
  return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
});

/** Features of every LOADED layer (enabled or not) within `km` of a point, nearest first. */
export function nearby(lat: number, lon: number, km: number, excludeKey?: string): { f: Feature; dist: number }[] {
  const out: { f: Feature; dist: number }[] = [];
  for (const id of LAYER_ORDER) {
    for (const f of featuresByLayer.value[id]) {
      if (f.key === excludeKey) continue;
      const d = haversineKm(lat, lon, f.lat, f.lon);
      if (d <= km) out.push({ f, dist: d });
    }
  }
  return out.sort((a, b) => a.dist - b.dist);
}

// ---- Events: GAA matches + horse racing and eventing (data/events.ts) ----
// Two feeds, one shape. The date window, the facet chips, the "What's on" list and the
// rings on the map all read `allEvents`, so a third feed only has to be adapted and
// loaded — see data/events.ts.
export const fixturesData = signal<FixturesData | null>(null);
export const equineData = signal<EquineData | null>(null);

export function localISODate(d = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
/** ISO date `n` days after `iso` (n may be negative). */
export function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  return localISODate(new Date(y, m - 1, d + n));
}
export const FAR_FUTURE = "9999-12-31";

/** The events panel replaces the results list while it is on. */
export const eventsOpen = signal(false);
/** Feeds shown; empty means all of them. */
export const eventFeeds = signal<Set<string>>(new Set());
/** Facets, feed-qualified ("gaa:hurling", "equine:race_meeting"). Empty means all. */
export const eventCats = signal<Set<string>>(new Set());
export const eventBands = signal<Set<string>>(new Set());
/** A venue name to narrow the list to. */
export const eventVenueName = signal<string | null>(null);

/** The trip window: ISO dates, both optional. `from` unset means "from today". */
export const eventFrom = signal<string | null>(null);
export const eventTo = signal<string | null>(null);
export function setEventWindow(from: string | null, to: string | null): void {
  batch(() => { eventFrom.value = from; eventTo.value = to; });
}
export function clearEventWindow(): void { setEventWindow(null, null); }
/** The window the list uses: open-ended forward unless an end date is set. */
export const listWindow = computed<[string, string]>(() => {
  const today = localISODate();
  const from = eventFrom.value && eventFrom.value > today ? eventFrom.value : today;
  return [from, eventTo.value || FAR_FUTURE];
});
/** The window the map rings use: a fortnight when no trip end is set, so the island is
 *  not covered in rings for the whole autumn. */
export const ringWindow = computed<[string, string]>(() => {
  const [from] = listWindow.value;
  return [from, eventTo.value || addDays(from, 14)];
});
export const eventWindowSet = computed(() => eventFrom.value != null || eventTo.value != null);

/** Every event still to come, from every feed, oldest first. */
export const allEvents = computed<EventItem[]>(() => {
  const today = localISODate();
  const out: EventItem[] = [];
  for (const f of fixturesData.value?.fixtures ?? []) if (f.status === "fixture") out.push(fixtureToEvent(f));
  for (const e of equineData.value?.events ?? []) if (e.status === "scheduled") out.push(equineToEvent(e));
  return out.filter((e) => e.endDate >= today).sort(byWhen);
});
function byWhen(a: EventItem, b: EventItem): number {
  return a.date.localeCompare(b.date) || (a.time || "99:99").localeCompare(b.time || "99:99") || a.title.localeCompare(b.title);
}

/** Feeds whose layer is switched on — a feed's events follow its layer. */
const liveFeeds = computed<Set<string>>(() => new Set(FEED_ORDER.filter((f) => enabledLayers.value.has(FEEDS[f].layer))));

/** In the trip window and in a live feed: what the counts and the chips are drawn from. */
export const eventsInWindow = computed<EventItem[]>(() => {
  const [from, to] = listWindow.value;
  const live = liveFeeds.value;
  return allEvents.value.filter((e) => live.has(e.feed) && inWindow(e, from, to));
});

/** Which feeds have a facet ticked — worked out once, not once per event. */
const feedsWithFacet = (set: Set<string>): Set<string> => {
  const out = new Set<string>();
  for (const k of set) out.add(k.slice(0, k.indexOf(":")));
  return out;
};
const catFeeds = computed(() => feedsWithFacet(eventCats.value));
const bandFeeds = computed(() => feedsWithFacet(eventBands.value));

/** A facet set only constrains its own feed, so ticking "Hurling" never hides the races. */
function passesFacets(e: EventItem): boolean {
  const feeds = eventFeeds.value;
  if (feeds.size && !feeds.has(e.feed)) return false;
  if (catFeeds.value.has(e.feed) && !eventCats.value.has(facetKey(e.feed, e.category))) return false;
  if (bandFeeds.value.has(e.feed) && !eventBands.value.has(facetKey(e.feed, e.band))) return false;
  return true;
}

/** What the "What's on" list shows. */
export const eventList = computed<EventItem[]>(() => {
  const v = eventVenueName.value;
  return eventsInWindow.value.filter((e) => passesFacets(e) && (!v || e.venue.name === v));
});

/** Every event and result at one of our own venues, by "<layer>:<id>". */
export const eventsByPlace = computed<Map<string, EventItem[]>>(() => {
  const m = new Map<string, EventItem[]>();
  const add = (e: EventItem) => {
    if (!e.venue.feature) return;
    const k = `${FEEDS[e.feed].layer}:${e.venue.feature}`;
    const a = m.get(k) ?? [];
    a.push(e);
    m.set(k, a);
  };
  // Results too, so a ground's page can show what happened here recently.
  for (const f of fixturesData.value?.fixtures ?? []) add(fixtureToEvent(f));
  for (const e of equineData.value?.events ?? []) add(equineToEvent(e));
  for (const a of m.values()) a.sort(byWhen);
  return m;
});

/** The next thing on at each of our own venues, as an ISO date — for the list cards. */
export const nextAtPlace = computed<Map<string, string>>(() => {
  const today = localISODate();
  const m = new Map<string, string>();
  for (const e of allEvents.value) {
    if (!e.venue.feature || e.endDate < today) continue;
    const k = `${FEEDS[e.feed].layer}:${e.venue.feature}`;
    if (!m.has(k)) m.set(k, e.date);
  }
  return m;
});

export interface EventVenueMarker {
  feed: EventFeed;
  name: string;
  lat: number;
  lon: number;
  /** id in that feed's layer, when the venue is one of ours. */
  place?: string;
  n: number;
  next: EventItem;
}
/** Venues with something on inside the ring window — the rings on the map. */
export const eventVenues = computed<EventVenueMarker[]>(() => {
  const [from, to] = ringWindow.value;
  const m = new Map<string, EventVenueMarker>();
  for (const e of allEvents.value) {
    if (e.venue.lat == null || e.venue.lon == null) continue;
    if (!inWindow(e, from, to) || !passesFacets(e)) continue;
    if (!liveFeeds.value.has(e.feed)) continue;
    const k = `${e.feed}|${e.venue.feature ?? `${e.venue.lat.toFixed(4)},${e.venue.lon.toFixed(4)}`}`;
    const cur = m.get(k);
    if (cur) cur.n++;
    else m.set(k, { feed: e.feed, name: e.venue.name, lat: e.venue.lat, lon: e.venue.lon, place: e.venue.feature, n: 1, next: e });
  }
  return [...m.values()];
});

/** A ringed venue with no pin of its own: clicking its ring opens a sheet of what is on
 *  there. Cleared when a feature is selected. */
export const selectedVenue = signal<EventVenueMarker | null>(null);
